import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/store/api/baseQuery';
import { postCardSecureTokenize } from '@/utils/residents/payment/paymentsGateway';
import { leasesApi } from '@/store/api/leasesApi';

// Backend envelopes the payment-processor response inside `response` (see
// `payments.service.ts`: `return { ...response, paymentId }`). Shape is
// identical for the SOAP and REST Yardi paths — both go through the same
// CardConnect `authorizePayment` before any PMS sync. Every field is optional
// because CardConnect occasionally omits fields on partial-success paths; the
// consumer must guard before using them.
export interface PostPayBalanceResponse {
  success: boolean;
  response?: {
    paymentId?: number;
    amount?: string;
    respstat?: string; // "A" = approved
    respcode?: string; // "000" = approved
    resptext?: string;
    retref?: string;
    authcode?: string;
    orderId?: string;
    merchid?: string;
    receipt?: string;
    [key: string]: unknown;
  };
}

export const webPaymentsCompatApi = createApi({
  reducerPath: 'webPaymentsCompatApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['paymentMethods', 'feeStructure', 'autoPayStatus'],
  endpoints: (builder) => ({
    getPaymentMethods: builder.query<any, void>({
      query: () => ({ url: '/api/v3/payments/payment-methods', method: 'GET' }),
      providesTags: ['paymentMethods'],
    }),
    getFeeStructure: builder.query<any, void>({
      query: () => ({ url: '/api/v3/payments/fee-structure', method: 'GET' }),
      providesTags: ['feeStructure'],
    }),
    addBankAccount: builder.mutation<any, { name: string; token: string; isDefault: boolean; accttype: string }>({
      query: (body) => ({ url: '/api/v3/payments/payment-account', method: 'POST', body }),
      invalidatesTags: ['paymentMethods'],
    }),
    addCreditCard: builder.mutation<any, { name: string; token: string; expiry: string; isDefault: boolean }>({
      query: (body) => ({ url: '/api/v3/payments/payment-card', method: 'POST', body }),
      invalidatesTags: ['paymentMethods'],
    }),
    postPayBalance: builder.mutation<PostPayBalanceResponse, { amount: number; leaseId: number; sourceId: number; residentId?: number }>({
      query: (body) => ({ url: '/api/v3/payments', method: 'POST', body }),
      invalidatesTags: ['paymentMethods'],
      async onQueryStarted({ leaseId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(leasesApi.util.invalidateTags([{ type: 'LeaseBalance', id: leaseId }]));
        } catch {
          // Payment failed; balance unchanged, no invalidation needed.
        }
      },
    }),
    setupAutoPay: builder.mutation<any, { 
      leaseId: number; 
      sourceId: number; 
      residentId?: number; 
      autopayIsFullAmount: boolean;
      autopayIsLeaseLifetime: boolean;
      fixedAmount?: number;
      startDate?: string;
      endDate?: string;
    }>({
      query: ({ residentId, sourceId, autopayIsFullAmount, autopayIsLeaseLifetime, fixedAmount, startDate, endDate, leaseId }) => ({
        url: residentId ? `/api/v2/payments/autopay/${residentId}` : '/api/v2/payments/autopay',
        method: 'POST',
        body: {
          sourceId,
          auto_pay_full_amount: autopayIsFullAmount,
          auto_pay_lifetime_of_lease: autopayIsLeaseLifetime,
          leaseId,
          fixed_amount: fixedAmount,
          start_date: startDate,
          end_date: endDate,
        },
      }),
      invalidatesTags: ['autoPayStatus'],
    }),
    cancelAutoPay: builder.mutation<any, number>({
      query: (id) => ({ url: `/api/v2/payments/autopay/${id}`, method: 'DELETE' }),
      invalidatesTags: ['autoPayStatus'],
    }),
    getAutoPayStatus: builder.query<any, void>({
      query: () => ({ url: '/api/v2/payments/autopay', method: 'GET' }),
      providesTags: ['autoPayStatus'],
    }),
    getAutoPayStatusByLease: builder.query<any, { leaseId: number }>({
      query: ({ leaseId }) => ({ url: `/api/v2/payments/autopay/lease/${leaseId}`, method: 'GET' }),
      providesTags: ['autoPayStatus'],
    }),
    putBankAccount: builder.mutation<any, { sourceId: string | number; bankAccount: { isDefault: boolean } }>({
      query: ({ sourceId, bankAccount }) => ({ url: `/api/v3/payments/payment-account/${sourceId}`, method: 'PUT', body: bankAccount }),
      invalidatesTags: ['paymentMethods'],
    }),
    putCreditCard: builder.mutation<any, { sourceId: string | number; creditCard: { isDefault: boolean } }>({
      query: ({ sourceId, creditCard }) => ({ url: `/api/v3/payments/payment-card/${sourceId}`, method: 'PUT', body: creditCard }),
      invalidatesTags: ['paymentMethods'],
    }),
    deleteBankAccount: builder.mutation<any, { sourceId: string | number }>({
      query: ({ sourceId }) => ({ url: `/api/v3/payments/payment-account/${sourceId}`, method: 'DELETE' }),
      invalidatesTags: ['paymentMethods'],
    }),
    deleteCreditCard: builder.mutation<any, { sourceId: string | number }>({
      query: ({ sourceId }) => ({ url: `/api/v3/payments/payment-card/${sourceId}`, method: 'DELETE' }),
      invalidatesTags: ['paymentMethods'],
    }),
    // CardConnect tokenization. Deliberately NOT a `query` through
    // baseQueryWithReauth: that client sends cookies (`credentials: 'include'`)
    // plus the resident's bearer token, and CardSecure's CORS policy rejects
    // both in a browser (mobile web could never add a card) while native fetch
    // let the JWT leak to a third party. `postCardSecureTokenize` owns the
    // request shape CardSecure accepts; this only maps its result onto RTK's
    // `{ data } | { error }` so the calling forms keep their contract.
    generateCardConnectToken: builder.mutation<
      {
        errorcode: number;
        message: string;
        token: string;
      },
      {
        account: string;
        cvv?: string;
        expiry?: string;
      }
    >({
      queryFn: async (body, { signal }) => {
        try {
          const response = await postCardSecureTokenize(body, signal);
          const data = await response.json();
          if (!response.ok) {
            return { error: { status: response.status, data } };
          }
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(error) } };
        }
      },
    }),
  }),
});

export const {
  useGetPaymentMethodsQuery,
  useLazyGetPaymentMethodsQuery,
  useGetFeeStructureQuery,
  useLazyGetFeeStructureQuery,
  useAddBankAccountMutation,
  useAddCreditCardMutation,
  usePostPayBalanceMutation,
  useSetupAutoPayMutation,
  useCancelAutoPayMutation,
  useGetAutoPayStatusQuery,
  useGetAutoPayStatusByLeaseQuery,
  usePutBankAccountMutation,
  usePutCreditCardMutation,
  useDeleteBankAccountMutation,
  useDeleteCreditCardMutation,
  useGenerateCardConnectTokenMutation,
} = webPaymentsCompatApi;


